// Polyfill Array.prototype.at() for older browsers (Safari < 15.4, Chrome < 92)
// Required by web-vitals dependency via posthog-js
if (!Array.prototype.at) {
  // eslint-disable-next-line no-extend-native
  Array.prototype.at = function (index: number) {
    const length = this.length;
    const k = index >= 0 ? index : length + index;
    return k >= 0 && k < length ? this[k] : undefined;
  };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import './app/globals.css';

// Initialize Bugsnag first (before any errors can occur)
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

Bugsnag.start({
  apiKey: 'fe8dd4c886f45623657f94e003c5aef4',
  plugins: [new BugsnagPluginReact()],
  appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
  releaseStage: import.meta.env.MODE || 'development',
  enabledReleaseStages: ['production', 'staging'],
  enabledBreadcrumbTypes: ['navigation', 'user', 'state', 'error'],
  maxBreadcrumbs: 50,
  // Redact sensitive data from error reports
  redactedKeys: [
    'password',
    'password_confirmation',
    'secret',
    'api_key',
    'apiKey',
    'access_token',
    'accessToken',
    'refresh_token',
    'refreshToken',
    'ssn',
    'social_security',
    'credit_card',
    'creditCard',
    'cvv',
    'pin'
  ],
  onError: (event) => {
    // Add global context to all errors
    event.addMetadata('app', {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Don't report chunk load errors (they auto-refresh)
    const errorMessage = event.errors[0]?.errorMessage || '';
    if (errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Importing a module script failed') ||
        errorMessage.includes('error loading dynamically imported module') ||
        errorMessage.includes('not a valid JavaScript MIME type') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError') ||
        // React.lazy payload failed to resolve (stale/partial chunk) — now
        // recognized as a chunk error and auto-reloaded by the boundary.
        errorMessage.includes('_result.default') ||
        errorMessage.includes("reading 'default'")) {
      return false; // Don't send to Bugsnag — handled by the chunk auto-reload
    }

    // Cross-origin "Script error." — unactionable third-party/extension noise
    // (CORS hides the real message/stack). Our own module scripts are CORS-enabled,
    // so these are never our code. This is the single largest error reporter.
    if (errorMessage === 'Script error.' || errorMessage.includes('Script error')) {
      return false;
    }

    // Browser storage / permission denied (private mode, blocked APIs) — not ours.
    if (errorMessage.includes('The request was denied')) {
      return false;
    }

    // iOS/Android in-app browser & native-bridge noise: WKWebView
    // messageHandlers, Android JS-interface teardown. Injected by the host app,
    // not our code (PostHog showed ~26 webkit.messageHandlers events).
    if (errorMessage.includes('window.webkit.messageHandler') ||
        errorMessage.includes('Java object is gone') ||
        errorMessage.includes('enableDidUserTypeOnKeyboardLogging')) {
      return false;
    }

    // Don't report web-vitals errors from older browsers - already handled by polyfill
    if (errorMessage.includes('.at is not a function') ||
        errorMessage.includes('entries.at is not a function') ||
        errorMessage.includes('web-vitals')) {
      return false; // Don't send to Bugsnag
    }

    // Don't report browser extension errors (outerHTML manipulation, invalid regex from injected scripts)
    if (errorMessage.includes("reading 'outerHTML'") ||
        errorMessage.includes('Invalid regular expression') ||
        errorMessage.includes('Java object is gone')) {
      return false; // Don't send to Bugsnag
    }

    // Don't report third-party script errors (ad networks, analytics, browser extensions)
    // bird_gj is an obfuscated function from external scripts, offsetHeight errors from injected code
    if (errorMessage.includes("reading 'offsetHeight'") ||
        errorMessage.includes('bird_gj')) {
      return false; // Don't send to Bugsnag
    }

    // Don't report iOS WebView errors when native handlers aren't configured
    if (errorMessage.includes('webkit.messageHandlers') ||
        errorMessage.includes("evaluating 'window.webkit.messageHandlers'")) {
      return false; // Don't send to Bugsnag
    }

    // Don't report third-party WebView bridge errors (Facebook/Instagram in-app browser)
    if (errorMessage.includes('WebViewJavascriptBridge')) {
      return false;
    }

    // Don't report InvalidAccessError from postMessage (Bugsnag perf lib in restricted contexts)
    if (event.errors[0]?.errorClass === 'InvalidAccessError') {
      const stackStr = JSON.stringify(event.errors[0]?.stacktrace || []);
      if (stackStr.includes('postMessage')) {
        return false;
      }
    }

    // Don't report Safari internal errors (password autofill, Keychain)
    if (event.errors[0]?.stacktrace?.some((frame: { file?: string }) =>
      frame.file?.includes('webkit-masked-url')
    )) {
      return false;
    }

    // Don't report CookieYes third-party script errors
    if (errorMessage.includes('CookieYes') || errorMessage.includes('cookieyes.com') ||
        event.errors[0]?.stacktrace?.some((frame: { file?: string }) =>
          frame.file?.includes('cookieyes.com')
        )) {
      return false;
    }

    // Don't report generic SecurityErrors from browser blocking requests (CORS, extensions, privacy)
    if (event.errors[0]?.errorClass === 'SecurityError' &&
        errorMessage.includes('The request was denied')) {
      return false; // Don't send to Bugsnag
    }

    // Don't report generic "Script error." which is CORS masking external script errors
    if (errorMessage === 'Script error.' || errorMessage === 'Script error') {
      return false; // Don't send to Bugsnag
    }

    // Don't report sessionStorage access errors (private browsing, blocked cookies)
    if (errorMessage.includes("sessionStorage") ||
        errorMessage.includes("Access is denied for this document")) {
      return false; // Don't send to Bugsnag
    }

    // Don't report Safari/network "Load failed" or "Failed to fetch" errors (no useful stack trace)
    if (errorMessage === 'Load failed' || errorMessage === 'Failed to fetch') {
      return false; // Don't send to Bugsnag
    }

    // Don't report SyntaxErrors from root HTML page (bot/extension noise from BugsnagPerformance CDN)
    if (event.errors[0]?.errorClass === 'SyntaxError') {
      const stackFile = event.errors[0]?.stacktrace?.[0]?.file || '';
      if (stackFile.endsWith('/') || stackFile.match(/\/:[\d]+$/)) {
        return false; // Don't send to Bugsnag
      }
    }

    // Don't report errors from in-app browsers (Instagram, Facebook, etc.) injecting JS
    const stacktrace = event.errors[0]?.stacktrace || [];
    if (stacktrace.some((frame: { file?: string }) =>
      frame.file?.includes('iabjs://') || frame.file?.includes('injectedScript')
    )) {
      return false;
    }

    // Don't report errors injected by browser extensions (password managers,
    // autofill, etc.) — not our code. Match by extension stack frames or by the
    // tell-tale symbols those extensions reference (e.g. "xbrowser is not
    // defined" from execute_auto_fill). Bugsnag #1426 and similar.
    if (stacktrace.some((frame: { file?: string; method?: string }) =>
      frame.file?.includes('chrome-extension://') ||
      frame.file?.includes('moz-extension://') ||
      frame.file?.includes('safari-extension://') ||
      frame.file?.includes('safari-web-extension://') ||
      frame.method?.includes('execute_auto_fill')
    )) {
      return false;
    }
    // Generic extension symbols: "<x>browser is not defined" (xbrowser,
    // swbrowser, …) and CSS/JS injectors. Bugsnag #1426, #1425.
    if (/\b[a-z]*browser is not defined\b/i.test(errorMessage) ||
        errorMessage.includes("Can't find variable: xbrowser") ||
        errorMessage.includes('execute_auto_fill') ||
        errorMessage.includes('needInjectCss')) {
      return false;
    }

    // Expected user error, not an app bug: wrong email/password at sign-in.
    // Bugsnag #1306 — don't report invalid-credential auth failures.
    if (errorMessage.includes('Invalid login credentials')) {
      return false;
    }

    // Don't report ResizeObserver loop errors (benign browser internal)
    if (errorMessage.includes('ResizeObserver loop') ||
        errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
      return false;
    }

    // Don't report errors from localhost (dev environment leaking to production Bugsnag)
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return false;
    }

    // Don't report errors with no application code (line 0 only, third-party noise)
    if (stacktrace.length > 0 &&
        stacktrace.every((frame: { file?: string; lineNumber?: number }) =>
          !frame.file || frame.file === ':0' || frame.lineNumber === 0
        )) {
      return false;
    }

    return true;
  }
});

// Get the Bugsnag React error boundary component
const BugsnagErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

// Initialize i18n before rendering
import './lib/i18n';

// Capture UTM parameters for attribution tracking
import { captureUTMParams } from './lib/utm';
captureUTMParams();

// Build marker — rotates the hashed JS filename to bypass poisoned edge cache
// from the 2026-06-15 deploy-propagation incident.
(window as unknown as Record<string, unknown>).__buildId = '20260615-1700';

// Load GTM test utility for console debugging
import('./lib/gtm-test');

// Handle chunk load errors after deployments (issue #52)
// When new code is deployed, old chunks may no longer exist, causing dynamic import failures
// This handler auto-refreshes once to load the new chunks
import {
  isChunkLoadError,
  handleChunkLoadError,
  clearChunkErrorFlag,
} from './lib/errorHandling/chunkErrorHandler';

// Proactive version detection to prompt users before chunk errors occur
import { initVersionDetection } from './lib/errorHandling/versionDetection';

// Clear the refresh flag on successful page load
clearChunkErrorFlag();

// Initialize version detection (only in production to avoid dev noise)
if (import.meta.env.MODE === 'production') {
  initVersionDetection();
}

// Handle synchronous errors (e.g., script tag failures)
window.addEventListener('error', (event) => {
  if (event.message && isChunkLoadError(event.message)) {
    handleChunkLoadError();
    // If handling (refreshing), prevent default error reporting
    event.preventDefault();
  } else {
    // Report non-chunk errors to Bugsnag
    Bugsnag.notify(event.error || new Error(event.message), (bugsnagEvent) => {
      bugsnagEvent.context = 'window.error';
      bugsnagEvent.addMetadata('event', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
  }
});

// Vite dispatches 'vite:preloadError' when a dynamically imported chunk fails to
// load — the most reliable signal for stale chunks after a deploy. This covers
// lazy routes that don't use lazyWithRetry (marketing, /attorneys, /law-firms)
// and cases where React's error boundary would otherwise swallow the rejection
// before our window handlers see it. PostHog showed ~100+ of these in 14 days.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault(); // stop Vite from throwing; we recover by reloading
  handleChunkLoadError(); // guarded one-time reload to fetch fresh chunks
});

// Handle promise rejections from dynamic imports
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || String(event.reason);
  if (isChunkLoadError(reason)) {
    const isHandling = handleChunkLoadError();
    // If handling (refreshing), prevent default error reporting
    if (isHandling) {
      event.preventDefault();
    }
  } else if (reason === 'Load failed' || reason === 'Failed to fetch') {
    // Network-level errors with no useful stack trace (Safari fetch abort, connectivity)
    event.preventDefault();
  } else {
    // Report non-chunk promise rejections to Bugsnag
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    Bugsnag.notify(error, (bugsnagEvent) => {
      bugsnagEvent.context = 'unhandledrejection';
    });
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BugsnagErrorBoundary
      FallbackComponent={({ error, info, clearError }) => (
        <div style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{
            maxWidth: '600px',
            padding: '2rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e293b' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
              We've been notified and are working on a fix. Please try refreshing the page.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0f172a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Refresh Page
              </button>
              {clearError && (
                <button
                  onClick={clearError}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'white',
                    color: '#0f172a',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
            {import.meta.env.DEV && (
              <details style={{ marginTop: '1.5rem', fontSize: '0.875rem' }}>
                <summary style={{ cursor: 'pointer', color: '#64748b', marginBottom: '0.5rem' }}>
                  Error Details (Dev Only)
                </summary>
                <pre style={{
                  padding: '1rem',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '0.75rem'
                }}>
                  {error.toString()}
                  {'\n\n'}
                  {info?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    >
      <RouterProvider router={router} />
    </BugsnagErrorBoundary>
  </React.StrictMode>
);
